import React from "react";
import useLang from "../../../../hooks/useLang";

/**
 * The "N duplicates were merged into X" card posted to the conversation
 * timeline when a merge completes (EAI-1815 / EAI-1816).
 *
 * Built to the design canvas "Merged Timeline Event" / "Lead Merged Timeline
 * Event" screens. Both are the same card with different nouns, so the
 * opportunity and lead versions render through here rather than the two
 * headerless antd `Table`s with hardcoded green cells they used to be.
 *
 * Layout: a summary sentence naming the surviving record, then one entry per
 * archived source — a 13px semibold title line ("EAI 3423305 Opportunity:
 * Shashik EAI-Test" / "Shashik E-Test") over a 12px grey meta line
 * ("Internet / Dealer Inspire · Created on Aug 20, 2026").
 *
 * No box of its own: this renders INSIDE the otto message shell, which already
 * supplies a rounded bordered card. A second one just boxed a box.
 */

/**
 * Type scale lifted from the canvas, so the event matches the rest of the merge
 * surfaces rather than inheriting the thread's 14px/22px body text:
 *   summary  14px / 22px, rgba(0,0,0,0.88)
 *   title    14px / 22px, 600
 *   meta     12px / 18px, rgba(0,0,0,0.65) — status and created date stay small
 */
const INK = "rgba(0,0,0,0.88)";
const META_INK = "rgba(0,0,0,0.65)";

/**
 * @param {number} count - how many records were merged in.
 * @param {"opportunity"|"lead"} recordType - which noun the sentence uses.
 * @param {string} masterLabel - the surviving record, e.g. "DS 1471841".
 * @param {string} [masterHref] - its CRM link, when it has one.
 * @param {{key: string, title: string, meta: string}[]} rows - one per archived
 *   source record: a bold identifying line over a grey detail line.
 */
const MergedTimelineEvent = ({ count, recordType, masterLabel, masterHref, rows = [] }) => {
  const [lang] = useLang();

  /**
   * Four templates per record type: one that names the survivor and one that
   * does not, each singular and plural.
   *
   * The no-record pair is not cosmetic. Every merge message written before the
   * server started sending `master_label` has no survivor to name, and those
   * rows are permanent history — stripping the placeholder out of the normal
   * sentence left them reading "…were merged into. Notes, tasks…", so the
   * sentence without a record has to be its own translation rather than the
   * other one with a hole cut in it.
   */
  const isLead = recordType === "lead";
  const template = masterLabel
    ? (isLead &&
        (count === 1 ? lang.mergedLeadTimelineSummarySingular : lang.mergedLeadTimelineSummary)) ||
      (!isLead &&
        (count === 1
          ? lang.mergedOpportunityTimelineSummarySingular
          : lang.mergedOpportunityTimelineSummary))
    : (isLead &&
        (count === 1
          ? lang.mergedLeadTimelineSummaryNoRecordSingular
          : lang.mergedLeadTimelineSummaryNoRecord)) ||
      (!isLead &&
        (count === 1
          ? lang.mergedOpportunityTimelineSummaryNoRecordSingular
          : lang.mergedOpportunityTimelineSummaryNoRecord));

  // The sentence names the surviving record mid-string, so split on the
  // placeholder rather than interpolating markup into the translation.
  const sentence = (template || "").replace("{{count}}", count);
  const [before, after = ""] = sentence.split("{{record}}");

  return (
    <div className="w-full">
      <div className="pb-3 text-sm leading-[22px]" style={{ color: INK }}>
        {before}
        {masterLabel &&
          (masterHref ? (
            <a href={masterHref} target="_blank" rel="noreferrer">
              {masterLabel}
            </a>
          ) : (
            <span className="font-medium">{masterLabel}</span>
          ))}
        {after}
      </div>

      {rows.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {rows.map(row => (
            <div key={row.key}>
              {row.title && (
                <div className="text-sm font-semibold leading-[22px]" style={{ color: INK }}>
                  {row.title}
                </div>
              )}
              {row.meta && (
                <div className="text-[12px] leading-[18px]" style={{ color: META_INK }}>
                  {row.meta}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MergedTimelineEvent;
